"use client"

import clsx from "clsx"
import type { ReactNode } from "react"
import type { ArticleTeaserModel } from "@/services/api/content/models/article-teaser-model.types"
import { ObservedBoundary } from "@/vendors/Snowplow/components/ObservedBoundary.component"
import { useSnowplowRecommendationInViewTracking } from "@/vendors/Snowplow/hooks/use-snowplow-recommendation-in-view-tracking.hook"

type RecommendationTrackingProps = {
  readonly id: string | null
  readonly teasers: ReadonlyArray<ArticleTeaserModel>
  readonly children: ReactNode
  readonly className?: string
  readonly position: "home-now" | "ralso"
  readonly threshold?: number
  readonly signature?: string | null
}

/**
 * Wrapper component for tracking recommendation visibility.
 *
 * Currently runs both V1 and V2 tracking simultaneously.
 * Once V1 is removed, this component can be replaced with just `ObservedBoundary`.
 *
 * @todo Remove V1 tracking and useMergedRefs when feature flag is 100% rolled out
 */
export function RecommendationTracking({
  children,
  id,
  teasers,
  className,
  position,
  threshold = 0.8,
  signature,
}: RecommendationTrackingProps) {
  // V1 tracking - TODO: Remove when V1 is deprecated
  const v1Ref = useSnowplowRecommendationInViewTracking({
    articleIds: teasers.map((article) => article.content.id),
    threshold,
    trackingId: id,
  })

  return (
    <ObservedBoundary
      boundaryId={`recommendation-${position}-${id}`}
      boundaryType="recommended-list"
      className={clsx("print:hidden", className)}
      data-k5a-pos={position}
      recommendationId={id ?? undefined}
      recommendationSignature={signature ?? undefined}
      ref={v1Ref}
    >
      {children}
    </ObservedBoundary>
  )
}
